import React, { useState, useEffect } from 'react';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail, validatePhoneNumber, validateAddress } from '../utils/validation';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';
import { Autocomplete } from '@react-google-maps/api';

const libraries = ['places'];
const ClientModal = ({ client, onClose, onSave }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    ...client,
  });
  const [errors, setErrors] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [autocomplete, setAutocomplete] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      client_phone: value
    }));
    if (errors.client_phone) {
      setErrors(prev => ({ ...prev, client_phone: null }));
    }
  };

  
  const handleAddressChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      client_address: value,
    }));
    setErrors((prev) => ({ ...prev, client_address: null }));
  };

  const onLoad = (autocomplete) => {
    setAutocomplete(autocomplete);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (!place.geometry || !place.geometry.location) {
        window.alert("No details available for input: '" + place.name + "'");
        return;
      }

      const formattedAddress = place.formatted_address;
      // Directly update the address, no need for client-side validation
      setFormData((prev) => ({
        ...prev,
        client_address: formattedAddress,
      }));
      setErrors((prev) => ({ ...prev, client_address: '' }));
    } else {
      console.log('Autocomplete is not loaded yet!');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Client name validation
    if (!formData.client_name?.trim()) {
      newErrors.client_name = 'Client name is required';
    }

    // Email validation
    if (formData.client_email) {
      const emailValidation = validateEmail(formData.client_email);
      if (!emailValidation.isValid) {
        newErrors.client_email = emailValidation.error;
      }
    }

    // Phone validation
    if (formData.client_phone) {
      const phoneValidation = validatePhoneNumber(formData.client_phone);
      if (!phoneValidation.isValid) {
        newErrors.client_phone = phoneValidation.error;
      }
    }

    // Address validation
    if (!formData.client_address) {
      newErrors.client_address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const isValid = validateForm();
    if (!isValid) {
      setSaving(false);
      return;
    }

    try {
      const phoneValidation = validatePhoneNumber(formData.client_phone);
      const formattedPhone = phoneValidation.isValid ? phoneValidation.formattedNumber : formData.client_phone;
      const clientData = {
        ...formData,
        client_phone: formattedPhone
      };
      if (client?.client_id) {
        // Update existing client
        const { error } = await supabase
          .from('clients')
          .update(clientData)
          .eq('client_id', client.client_id);

        if (error) throw error;
      } else {
        // Create new client
        const { error } = await supabase
          .from('clients')
          .insert([{ ...clientData, user_id: user.id }]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving client:', error);
      setError('Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6" style={{ boxShadow: shadows.xl }}>
        <h2 className="text-xl font-bold mb-4" style={{ color: THEME.text }}>
          {client ? 'Edit Client' : 'New Client'}
        </h2>

        {error && (
          <div 
            className="rounded-lg p-4 mb-4 text-sm"
            style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Client Name
            </label>
            <input
              type="text"
              name="client_name"
              value={formData.client_name}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Email
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                name="client_email"
                value={formData.client_email}
                onChange={handleInputChange}
                className="w-full pl-10 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Phone
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <PhoneIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="tel"
                name="client_phone"
                value={formData.client_phone}
                onChange={handlePhoneChange}
                placeholder="Enter phone number (e.g. +61412345678)"
                className="w-full pl-10 rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                style={{ 
                  borderColor: errors.client_phone ? THEME.error : undefined 
                }}
              />
              {errors.client_phone && (
                <p className="text-sm mt-1" style={{ color: THEME.error }}>
                  {errors.client_phone}
                </p>
              )}
            </div>
          </div>
          

          <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: THEME.text }}
                >
                  Business Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MapPinIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                    <input
                      type="text"
                      name="client_address"
                      value={formData.client_address}
                      onChange={handleAddressChange}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-primary focus:border-transparent"
                      style={{
                        borderColor: errors.client_address
                          ? THEME.error
                          : undefined,
                      }}
                    />
                  </Autocomplete>
                  {errors.client_address && (
                    <p className="text-sm mt-1" style={{ color: THEME.error }}>
                      {errors.client_address}
                    </p>
                  )}
                </div>
              </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: THEME.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              style={{ 
                backgroundColor: THEME.primary,
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Save Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function Clients() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { logEvent } = useAnalytics();

  useEffect(() => {
    if (user) {
      fetchClients();
    }
  }, [user]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('client_name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      logEvent('Clients', 'Delete Client', `Client ID: ${clientId}`);
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('client_id', clientId);

      if (error) throw error;
      await fetchClients();
      logEvent('Clients', 'Delete Client Success', `Client ID: ${clientId}`);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Failed to delete client');
      logEvent('Clients', 'Delete Client Error', error.message);
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setSelectedClient(null);
    setShowModal(false);
  };

  const handleSave = async () => {
    await fetchClients();
    handleCloseModal();
  };

  const handleAddClient = async (clientData) => {
    try {
      logEvent('Clients', 'Add Client', 'New Client Creation');
      // ... existing add client code ...
      logEvent('Clients', 'Add Client Success', clientData.name);
    } catch (error) {
      logEvent('Clients', 'Add Client Error', error.message);
    }
  };

  const handleEditClient = async (clientId, clientData) => {
    try {
      logEvent('Clients', 'Edit Client', `Client ID: ${clientId}`);
      // ... existing edit client code ...
      logEvent('Clients', 'Edit Client Success', clientData.name);
    } catch (error) {
      logEvent('Clients', 'Edit Client Error', error.message);
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      logEvent('Clients', 'Delete Client', `Client ID: ${clientId}`);
      // ... existing delete client code ...
      logEvent('Clients', 'Delete Client Success', `Client ID: ${clientId}`);
    } catch (error) {
      logEvent('Clients', 'Delete Client Error', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>Clients</h1>
          <p style={{ color: THEME.subtext }}>Manage your client information</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
          style={{ 
            background: THEME.primary,
            boxShadow: shadows.md,
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          New Client
        </button>
      </div>

      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map((client) => (
          <div 
            key={client.client_id} 
            className="bg-white rounded-xl p-6"
            style={{ boxShadow: shadows.md }}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                  style={{ backgroundColor: `${THEME.primary}15` }}
                >
                  <UserGroupIcon className="w-6 h-6" style={{ color: THEME.primary }} />
                </div>
                <div>
                  <h3 className="font-semibold" style={{ color: THEME.text }}>
                    {client.client_name}
                  </h3>
                  <p className="text-sm" style={{ color: THEME.subtext }}>
                    {client.client_email}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(client)}
                  className="p-1 text-gray-400 hover:text-gray-500"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(client.client_id)}
                  className="p-1 text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {client.client_phone && (
              <div className="flex items-center text-sm mb-2" style={{ color: THEME.subtext }}>
                <PhoneIcon className="w-4 h-4 mr-2" />
                {client.client_phone}
              </div>
            )}

            {client.client_address && (
              <div className="flex items-start text-sm" style={{ color: THEME.subtext }}>
                <MapPinIcon className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                <p className="whitespace-pre-wrap">{client.client_address}</p>
              </div>
            )}
          </div>
        ))}

        {clients.length === 0 && (
          <div 
            className="col-span-full bg-white rounded-xl p-8 text-center"
            style={{ boxShadow: shadows.md }}
          >
            <UserGroupIcon className="w-12 h-12 mx-auto mb-4" style={{ color: THEME.primary }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: THEME.text }}>
              No Clients Yet
            </h3>
            <p className="mb-4" style={{ color: THEME.subtext }}>
              Add your first client to get started
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
              style={{ 
                background: THEME.primary,
                boxShadow: shadows.md,
              }}
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Client
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <ClientModal
          client={selectedClient}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
} 